import React from 'react';
import { MapPin, Star, Shield, ArrowRight } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import type { Service } from '../../types';

interface ServiceCardProps {
  service: Service;
}

export function ServiceCard({ service }: ServiceCardProps) {
  const location = useLocation();
  const mainImage = service.images?.[0] || 'https://images.unsplash.com/photo-1436491865332-7a61a109cc05';
  const truncatedDescription = service.description.length > 120 
    ? `${service.description.substring(0, 120)}...` 
    : service.description;

  return (
    <article className="group relative bg-white rounded-2xl sm:rounded-3xl shadow-lg hover:shadow-2xl transition-all duration-500 overflow-hidden hover:-translate-y-2">
      {/* Gradient border effect */}
      <div className="absolute inset-0 bg-gradient-to-r from-primary-500 via-blue-500 to-primary-500 rounded-2xl sm:rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 p-[1px]">
        <div className="w-full h-full bg-white rounded-2xl sm:rounded-3xl"></div>
      </div>
      
      <Link 
        to={`/services/${service.id}`} 
        state={{ from: location.pathname }}
        className="relative block h-full"
      >
        <header className="relative">
          <div className="relative h-40 sm:h-56 overflow-hidden rounded-t-2xl sm:rounded-t-3xl">
            <img
              src={mainImage}
              alt={`${service.title} at ${service.airportName || service.airportId}`}
              className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
              loading="lazy"
            />
            
            {/* Overlay gradient */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
            
            {/* Price badge */}
            <div className="absolute top-3 right-3 sm:top-4 sm:right-4">
              <div className="px-2.5 py-1.5 sm:px-4 sm:py-2 bg-white/95 backdrop-blur-sm rounded-full shadow-lg border border-white/20">
                <span className="text-xs sm:text-sm font-bold text-gray-900">
                  {service.contactForPrice ? 'Contact for price' : `$${service.price}`}
                </span>
              </div>
            </div>

            {/* Verified badge */}
            {service.provider.isVerified && (
              <div className="absolute top-3 left-3 sm:top-4 sm:left-4">
                <div className="flex items-center gap-1 px-2 py-1 sm:px-3 sm:py-1.5 bg-green-500/90 backdrop-blur-sm rounded-full">
                  <Shield size={10} className="text-white sm:w-3 sm:h-3" />
                  <span className="text-[10px] sm:text-xs font-medium text-white">Verified</span>
                </div>
              </div>
            )}

            {/* Location */}
            <div className="absolute bottom-3 left-3 sm:bottom-4 sm:left-4">
              <div className="flex items-center gap-1.5 sm:gap-2 px-2.5 py-1 sm:px-3 sm:py-1.5 bg-white/90 backdrop-blur-sm rounded-full">
                <MapPin size={12} className="text-primary-600 sm:w-3.5 sm:h-3.5" />
                <span className="text-xs sm:text-sm font-medium text-gray-900">
                  {service.airportName || service.airportId}
                </span>
              </div>
            </div>
          </div>
        </header>

        <div className="relative p-4 sm:p-6">
          <div className="mb-3 sm:mb-4">
            <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-1.5 sm:mb-2 group-hover:text-primary-600 transition-colors duration-300 line-clamp-2">
              {service.title}
            </h2>
            <p className="text-gray-600 text-xs sm:text-sm leading-relaxed line-clamp-3">
              {truncatedDescription}
            </p>
          </div>

          {/* Provider info */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-r from-primary-500 to-blue-600 rounded-full flex items-center justify-center">
                <span className="text-white font-semibold text-xs sm:text-sm">
                  {service.provider.name.charAt(0)}
                </span>
              </div>
              <div>
                <div className="font-medium text-gray-900 text-xs sm:text-sm line-clamp-1">
                  {service.provider.name}
                </div>
                <div className="flex items-center gap-1">
                  <Star size={10} className="fill-yellow-400 text-yellow-400 sm:w-3 sm:h-3" />
                  <span className="text-[10px] sm:text-xs text-gray-500">
                    {service.provider.rating} ({service.provider.reviews})
                  </span>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-1 text-primary-600 group-hover:text-primary-700 transition-colors duration-300">
              <span className="text-xs sm:text-sm font-medium hidden sm:inline">View Details</span>
              <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform duration-300 sm:w-4 sm:h-4" />
            </div>
          </div>
        </div>
      </Link>
    </article>
  );
}